import { Country, Maybe, TemporaryData, TransformType } from '../gql/generated/graphql';
import { formatMileage, formatMoney } from '../utils/helpers';
import { provincesArray } from './provinces';
import { statesArray } from './states';

type OverageTerms = { allowance: number; rate: number };

type CountryConfig = {
  regions: typeof statesArray;
  regionLabel: string;
  supportedRegions?: string[];
  postalCode: { label: string; mask?: string };
  hasCounty: boolean;
  // Plate-to-VIN lookup; without it the flow asks for the VIN only.
  plateLookup: boolean;
  // TaterTitle jurisdiction rules (e.g. walk-in requirements) exist for this country.
  jurisdictionLookup: boolean;
  addressAutofill: boolean;
  marketplaceFlow: boolean;
  payoffCollection: boolean;
  // SSN collection and the automated credit pull on the DOB step.
  creditPull: boolean;
  importTransformType?: TransformType;
  odometer: {
    placeholder: string;
    unit: string;
    max: number;
    overage: OverageTerms;
    feeCopy: {
      overageFees: string;
      noOverageFees: string;
      calculation: string;
    };
  };
};

const US_OVERAGE = { allowance: 36_000, rate: 0.2 };
// 20,000 km/yr over a 3-year lease.
const CA_OVERAGE = { allowance: 60_000, rate: 0.15 };

export const COUNTRY_CONFIG: Record<Country, CountryConfig> = {
  [Country.Us]: {
    regions: statesArray,
    regionLabel: 'State',
    postalCode: { label: 'ZIP' },
    hasCounty: true,
    plateLookup: true,
    jurisdictionLookup: true,
    addressAutofill: true,
    marketplaceFlow: true,
    payoffCollection: true,
    creditPull: true,
    odometer: {
      placeholder: 'Mileage',
      unit: 'miles',
      max: 150_000,
      overage: US_OVERAGE,
      feeCopy: {
        overageFees: 'mileage overage fees',
        noOverageFees: 'no over mileage fees',
        calculation: `This fee is calculated at the average ${formatMoney(
          US_OVERAGE.rate,
        )}/per mile that dealerships charge over the standard lease mileage allowance of ${formatMileage(
          US_OVERAGE.allowance,
        )} miles.`,
      },
    },
  },
  [Country.Ca]: {
    regions: provincesArray,
    regionLabel: 'Province',
    supportedRegions: ['AB'],
    postalCode: { label: 'Postal Code', mask: 'a9a 9a9' },
    hasCounty: false,
    plateLookup: false,
    jurisdictionLookup: false,
    addressAutofill: false,
    marketplaceFlow: false,
    payoffCollection: false,
    creditPull: false,
    importTransformType: TransformType.Canada,
    odometer: {
      placeholder: 'Kilometres',
      unit: 'kilometres',
      max: 240_000,
      overage: CA_OVERAGE,
      feeCopy: {
        overageFees: 'excess kilometre fees',
        noOverageFees: 'no excess kilometre fees',
        calculation: `This fee is calculated at the average ${formatMoney(
          CA_OVERAGE.rate,
        )} per kilometre that dealerships charge over the standard lease allowance of ${formatMileage(
          CA_OVERAGE.allowance,
        )} kilometres.`,
      },
    },
  },
};

export const getTempInfoCountry = (data?: Maybe<Pick<TemporaryData, 'country'>>): Country =>
  data?.country ?? Country.Us;

export const getTempInfoCountryConfig = (data?: Maybe<Pick<TemporaryData, 'country'>>) =>
  COUNTRY_CONFIG[getTempInfoCountry(data)];
